/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useRef, useState, useEffect } from 'react'
import { Save, Copy, FileDown, Square, ArrowUpCircle, SendHorizontal, Hash, X, FileCode, RotateCcw, Loader2, Cpu, Globe, GitBranch, FileText, Edit3 } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { JudgeLogo } from '@/ui/components/LLMLogos'
import { FilePreviewModal } from '@/ui/components/FilePreviewModal'
import { RAGChat } from '@/ui/components/RAGChat'
import { BrainstormDocumentEditor } from '@/ui/components/BrainstormDocumentEditor'
import { useChatStore, type ChatMessage } from '@/ui/stores/chat-store'
import { getMessageText } from '@/providers/llm-provider'
import type { JudgeResult, MetricsEntry } from '@/ui/stores/orchestrator-store'
import { getModelName } from '@/lib/model-names'

interface AttachedFile {
  path: string
  content: string
  type?: 'text' | 'image'
  mimeType?: string
}

interface CenterPanelProps {
  activeTab: string
  chatInput: string
  messages: ChatMessage[]
  isLoading: boolean
  isOrchestrating: boolean
  onHighlightFile?: (filePath: string) => void
  onOpenFile?: (filePath: string, highlightLines: { start: number; end: number }) => void
  autoPeerReview: boolean
  includeDocs: boolean
  inflatedMarkdown: string
  isInflating: boolean
  isExportingPDF: boolean
  selectedInflatedDoc: string | null
  inflatedDocs: Record<string, string>
  copiedInflate: boolean
  copiedPreview: boolean
  judgeResult: JudgeResult | null
  metricsHistory: MetricsEntry[]
  cumulativeCost: number
  cumulativeTokens: number
  rubricContent: string | undefined
  peers: any[]
  judgeModel: string | undefined
  hasProject: boolean
  onSetActiveTab: (tab: string) => void
  onSetChatInput: (input: string) => void
  onSendMessage: (message: string, attachedFiles?: Array<{ path: string; content: string }>) => Promise<void>
  onStopStreaming: () => void
  onSetAutoPeerReview: (enabled: boolean) => void
  onSetIncludeDocs: (enabled: boolean) => void
  onRunOrchestration: (message: string, peers: any[], rubric: string | undefined, judgeModel: string | undefined, contextFiles?: string) => Promise<void>
  onSaveOrchestratorChat: () => void
  onCopyPeerResponse: (content: string) => void
  onInflatePeerResponse: (name: string, content: string) => void
  onSaveChatMessage: (message: ChatMessage) => void
  onSetApprovalSent: (sent: boolean) => void
  onHandleInflate: () => void
  onHandleLoadInflatedDoc: (fileName: string) => void
  onCopyInflatedMarkdown: () => void
  onExportPDF: () => void
  previewRef: React.RefObject<HTMLDivElement | null>
  onFilesChanged?: () => void // Callback to trigger file list refresh
}

export function CenterPanel({
  activeTab,
  chatInput,
  messages,
  isLoading,
  isOrchestrating,
  autoPeerReview,
  includeDocs,
  inflatedMarkdown,
  isInflating,
  isExportingPDF,
  selectedInflatedDoc,
  inflatedDocs,
  copiedInflate,
  copiedPreview,
  judgeResult,
  metricsHistory,
  cumulativeCost,
  cumulativeTokens,
  rubricContent,
  peers,
  judgeModel,
  hasProject,
  onSetActiveTab,
  onSetChatInput,
  onSendMessage,
  onStopStreaming,
  onSetAutoPeerReview,
  onSetIncludeDocs,
  onRunOrchestration,
  onSaveOrchestratorChat,
  onCopyPeerResponse,
  onInflatePeerResponse,
  onSaveChatMessage,
  onSetApprovalSent,
  onHandleInflate,
  onHandleLoadInflatedDoc,
  onCopyInflatedMarkdown,
  onExportPDF,
  previewRef,
  onFilesChanged,
  onHighlightFile,
  onOpenFile,
}: CenterPanelProps) {
  const chatEndRef = useRef<HTMLDivElement>(null)
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([])
  const [showFilePreview, setShowFilePreview] = useState(false)
  const [pendingAction, setPendingAction] = useState<'chat' | 'orchestration' | null>(null)
  const [showModelSelector, setShowModelSelector] = useState(false)
  const [showDocumentEditor, setShowDocumentEditor] = useState(false)
  const [documentContent, setDocumentContent] = useState('')
  const [documentFileName, setDocumentFileName] = useState('brainstorm-notes.md')
  const modelSelectorRef = useRef<HTMLDivElement>(null)
  const { clearMessages, mode, setMode, showHandoverModal, compiledDocument, setCompiledDocument, closeHandoverModal, approveHandover, brainstormMore, model, webSearchEnabled, setWebSearchEnabled, gitToolsEnabled, setGitToolsEnabled, fileToolsEnabled, setFileToolsEnabled, setModel } = useChatStore()

  // All models support web search via function calling (if search API is configured)
  // Some models (Perplexity, Grok) have built-in web search, others use tool calling
  const modelSupportsWebSearch = true

  // Close model selector when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modelSelectorRef.current && !modelSelectorRef.current.contains(event.target as Node)) {
        setShowModelSelector(false)
      }
    }

    if (showModelSelector) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showModelSelector])

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleClearChat = () => {
    if (confirm('Clear chat history? This will remove all messages and attached files from the conversation.')) {
      clearMessages()
      setAttachedFiles([])
      console.log('[Chat] Chat history cleared')
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Set dropEffect to allow dropping
    e.dataTransfer.dropEffect = 'copy'
    setIsDraggingOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingOver(false)
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingOver(false)

    try {
      // Check for file data from global variable (set during drag start)
      const draggedData = (window as any).__draggedFileData

      if (draggedData) {
        // Clear the global variable
        delete (window as any).__draggedFileData

        // Handle multiple files (array) or single file (object)
        if (Array.isArray(draggedData)) {
          // Multiple files
          setAttachedFiles((prev) => {
            const newFiles = draggedData.filter(
              (file: { filePath: string; content: string }) =>
                !prev.some((f) => f.path === file.filePath)
            ).map((file: { filePath: string; content: string }) => ({
              path: file.filePath,
              content: file.content,
              type: 'text' as const
            }))
            return [...prev, ...newFiles]
          })
        } else {
          // Single file (legacy support)
          const { filePath, content } = draggedData
          setAttachedFiles((prev) => {
            if (prev.some((f) => f.path === filePath)) {
              return prev // Already attached
            }
            return [...prev, { path: filePath, content, type: 'text' as const }]
          })
        }
        return
      }

      // Try to get Hivemind file data from dataTransfer
      const fileData = e.dataTransfer.getData('application/x-hivemind-file')

      if (fileData) {
        const { filePath, content } = JSON.parse(fileData)

        // Add to attached files if not already attached
        setAttachedFiles((prev) => {
          if (prev.some((f) => f.path === filePath)) {
            return prev // Already attached
          }
          return [...prev, { path: filePath, content, type: 'text' as const }]
        })
        return
      }

      // Handle native file drops from desktop
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files)
        const maxFileSize = 10 * 1024 * 1024 // 10MB limit

        // Import necessary functions
        const projectStoreModule = await import('@/ui/stores/project-store')
        const currentProject = projectStoreModule.useProjectStore.getState().currentProject

        if (!currentProject) {
          alert('Please create or open a project first.')
          return
        }

        const isCloudProject = !currentProject.fileSystemPath

        for (const file of files) {
          // Check file size
          if (file.size > maxFileSize) {
            alert(`File "${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum size is 10MB.`)
            continue
          }

          // Determine file type
          const isPDF = file.type === 'application/pdf'
          const isImage = file.type.startsWith('image/')
          const isBinary = isPDF || file.type.startsWith('application/') || file.type.startsWith('video/') || file.type.startsWith('audio/')

          // For cloud projects, upload to Firebase Storage
          if (isCloudProject) {
            const reader = new FileReader()

            reader.onerror = () => {
              console.error(`[Cloud Drop] Failed to read file: ${file.name}`)
              alert(`Failed to read file: ${file.name}`)
            }

            reader.onload = async (event) => {
              try {
                const { uploadFileToCloud } = await import('@/lib/cloud-storage')
                const arrayBuffer = event.target?.result as ArrayBuffer

                console.log(`[Cloud Drop] Uploading ${file.name} to cloud storage...`)

                // Upload to Firebase Storage
                const cloudFile = await uploadFileToCloud(
                  currentProject.id,
                  file.name,
                  arrayBuffer,
                  file.type || 'application/octet-stream'
                )

                console.log(`[Cloud Drop] ✅ Uploaded: ${file.name} -> ${cloudFile.url}`)

                // Add to attached files with cloud URL
                setAttachedFiles((prev) => {
                  if (prev.some((f) => f.path === file.name)) {
                    return prev // Already attached
                  }
                  return [...prev, {
                    path: file.name,
                    content: cloudFile.url, // Store cloud URL
                    type: isImage ? 'image' as const : 'text' as const,
                    mimeType: file.type,
                    cloudFile: true // Flag to indicate this is a cloud file
                  }]
                })

                // Note: onFilesChanged is for local file system, not needed for cloud
              } catch (error) {
                console.error(`[Cloud Drop] Upload failed for ${file.name}:`, error)
                alert(`Failed to upload ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`)
              }
            }

            // Read as ArrayBuffer for cloud uploads (works for all file types)
            reader.readAsArrayBuffer(file)
          } else {
            // For local file system projects
            const { getDirectoryHandle } = await import('@/lib/storage')
            const { writeFile } = await import('@/lib/filesystem')

            if (isImage) {
              // Handle image files - read as DataURL for preview
              const reader = new FileReader()

              reader.onerror = () => {
                console.error(`[Desktop Drop] Failed to read image: ${file.name}`)
                alert(`Failed to read image: ${file.name}`)
              }

              reader.onload = async (event) => {
                const base64 = event.target?.result as string
                setAttachedFiles((prev) => {
                  if (prev.some((f) => f.path === file.name)) {
                    return prev // Already attached
                  }
                  return [...prev, {
                    path: file.name,
                    content: base64,
                    type: 'image' as const,
                    mimeType: file.type
                  }]
                })

                // Save image to project directory
                try {
                  const dirHandle = await getDirectoryHandle(currentProject.id)
                  if (dirHandle) {
                    await writeFile(dirHandle, file.name, base64)
                    console.log(`[Desktop Drop] ✅ Saved image: ${file.name}`)
                    onFilesChanged?.()
                  }
                } catch (error) {
                  console.error(`[Desktop Drop] Failed to save image ${file.name}:`, error)
                }
              }

              reader.readAsDataURL(file)
            } else if (isBinary) {
              // Handle binary files (PDFs, etc.) - read as ArrayBuffer then convert to base64
              const reader = new FileReader()

              reader.onerror = () => {
                console.error(`[Desktop Drop] Failed to read binary file: ${file.name}`)
                alert(`Failed to read binary file: ${file.name}`)
              }

              reader.onload = async (event) => {
                const arrayBuffer = event.target?.result as ArrayBuffer
                // Convert ArrayBuffer to base64 for storage
                const bytes = new Uint8Array(arrayBuffer)
                let binary = ''
                for (let i = 0; i < bytes.length; i++) {
                  binary += String.fromCharCode(bytes[i])
                }
                const base64 = 'data:' + file.type + ';base64,' + btoa(binary)

                setAttachedFiles((prev) => {
                  if (prev.some((f) => f.path === file.name)) {
                    return prev // Already attached
                  }
                  return [...prev, {
                    path: file.name,
                    content: base64,
                    type: 'text' as const, // Store as text with base64
                    mimeType: file.type
                  }]
                })

                // Save binary file to project directory
                try {
                  const dirHandle = await getDirectoryHandle(currentProject.id)
                  if (dirHandle) {
                    await writeFile(dirHandle, file.name, base64)
                    console.log(`[Desktop Drop] ✅ Saved binary file: ${file.name}`)
                    onFilesChanged?.()
                  }
                } catch (error) {
                  console.error(`[Desktop Drop] Failed to save binary file ${file.name}:`, error)
                }
              }

              reader.readAsArrayBuffer(file)
            } else {
              // Handle text files
              const reader = new FileReader()

              reader.onerror = () => {
                console.error(`[Desktop Drop] Failed to read text file: ${file.name}`)
                alert(`Failed to read text file: ${file.name}`)
              }

              reader.onload = async (event) => {
                const content = event.target?.result as string
                setAttachedFiles((prev) => {
                  if (prev.some((f) => f.path === file.name)) {
                    return prev // Already attached
                  }
                  return [...prev, {
                    path: file.name,
                    content: content,
                    type: 'text' as const
                  }]
                })

                // Save text file to project directory
                try {
                  const dirHandle = await getDirectoryHandle(currentProject.id)
                  if (dirHandle) {
                    await writeFile(dirHandle, file.name, content)
                    console.log(`[Desktop Drop] ✅ Saved file: ${file.name}`)
                    onFilesChanged?.()
                  }
                } catch (error) {
                  console.error(`[Desktop Drop] Failed to save file ${file.name}:`, error)
                }
              }

              reader.readAsText(file)
            }
          }
        }
        return
      }

      // Fallback to plain text
      const text = e.dataTransfer.getData('text/plain')
      if (text) {
        onSetChatInput(chatInput ? `${chatInput}\n\n${text}` : text)
      }
    } catch (error) {
      console.error('Failed to handle drop:', error)
    }
  }

  const removeAttachedFile = (filePath: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.path !== filePath))
  }

  const handlePaste = async (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('text')

    // Check if pasted text looks like a file path
    if (pastedText && (pastedText.includes('/') || pastedText.includes('\\')) && !pastedText.includes('\n')) {
      // Try to load the file from the current project
      const { currentProject } = await import('@/ui/stores/project-store').then(m => ({ currentProject: m.useProjectStore.getState().currentProject }))

      if (currentProject?.fileSystemPath) {
        try {
          const { getDirectoryHandle } = await import('@/lib/storage')
          const { readFile } = await import('@/lib/filesystem')

          const dirHandle = await getDirectoryHandle(currentProject.id)
          if (dirHandle) {
            // Clean up the path (remove leading ./ or /)
            const cleanPath = pastedText.replace(/^\.?\//, '')
            const content = await readFile(dirHandle, cleanPath)

            if (content !== null) {
              e.preventDefault()

              // Add to attached files
              setAttachedFiles((prev) => {
                if (prev.some((f) => f.path === cleanPath)) {
                  return prev // Already attached
                }
                return [...prev, { path: cleanPath, content }]
              })
              return
            }
          }
        } catch (error) {
          console.log('Not a valid file path, treating as regular text')
        }
      }
    }
  }

  const handleSendWithAttachments = async () => {
    if (!chatInput.trim() && attachedFiles.length === 0) return
    if (isLoading) return

    // If files are attached, show preview modal first
    if (attachedFiles.length > 0) {
      console.log(`[CenterPanel] Opening file preview modal with ${attachedFiles.length} file(s):`)
      attachedFiles.forEach((file, idx) => {
        console.log(`  ${idx + 1}. ${file.path} (${file.content.length} chars)`)
      })
      setPendingAction('chat')
      setShowFilePreview(true)
      return
    }

    // No files, send directly
    const message = chatInput.trim()
    onSetChatInput('')
    // Don't reset approvalSent here - only reset when starting new orchestration
    await onSendMessage(message)
  }

  const handleConfirmFilePreview = async (editedFiles: Array<{ path: string; content: string }>) => {
    setShowFilePreview(false)
    const message = chatInput.trim()
    onSetChatInput('')
    setAttachedFiles([])

    console.log(`[CenterPanel] Sending ${editedFiles.length} file(s) after preview/compression:`)
    editedFiles.forEach((file, idx) => {
      console.log(`  ${idx + 1}. ${file.path} (${file.content.length} chars)`)
    })

    if (pendingAction === 'chat') {
      // Send to orchestrator chat
      console.log('[CenterPanel] Sending to orchestrator chat')
      // Don't reset approvalSent here - only reset when starting new orchestration
      await onSendMessage(message, editedFiles.length > 0 ? editedFiles : undefined)
    } else if (pendingAction === 'orchestration') {
      // Send to peer orchestration
      console.log('[CenterPanel] Sending to peer orchestration')
      // Reset approvalSent when starting a NEW orchestration
      onSetApprovalSent(false)
      let contextFiles: string | undefined
      if (includeDocs && editedFiles.length > 0) {
        contextFiles = editedFiles
          .map(file => `[File: ${file.path}]\n\`\`\`\n${file.content}\n\`\`\``)
          .join('\n\n')
      }
      await onRunOrchestration(message, peers, rubricContent, judgeModel, contextFiles)
    }

    setPendingAction(null)
  }

  return (
    <>
      <section className="flex-1 flex flex-col overflow-hidden min-w-[420px]">
        {/* Header with metrics */}
        <div className="bg-background-secondary border-b border-border px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 relative">
            {/* Clickable Orchestrator Icon with Model Selector */}
            <div className="relative" ref={modelSelectorRef}>
              <button
                onClick={() => setShowModelSelector(!showModelSelector)}
                className="flex items-center gap-2 hover:bg-background-hover rounded px-2 py-1 transition-colors group"
                title="Click to change orchestrator model"
              >
                <Cpu className="w-4 h-4 text-accent group-hover:text-accent-bright" />
                <div className="flex flex-col items-start">
                  <span className="text-xs font-semibold">Orchestrator</span>
                  <span className="text-[10px] text-foreground-muted">{getModelName(model)}</span>
                </div>
              </button>

              {/* Model Selector Dropdown */}
              {showModelSelector && (
                <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-lg shadow-lg z-50 w-80 max-h-96 overflow-y-auto">
                  <div className="p-2 border-b border-border bg-background-secondary">
                    <h3 className="text-xs font-semibold">Select Orchestrator Model</h3>
                  </div>
                  <div className="p-1">
                    {/* Premium - Best in Class */}
                    <div className="px-2 py-1 text-[10px] font-semibold text-foreground-muted">💎 Premium - Best in Class</div>
                    <button
                      onClick={() => { setModel('anthropic/claude-sonnet-4-5'); setShowModelSelector(false) }}
                      className={`w-full text-left px-3 py-1.5 text-xs rounded hover:bg-background-hover ${model === 'anthropic/claude-sonnet-4-5' ? 'bg-accent/20 text-accent' : ''}`}
                    >
                      Claude Sonnet 4.5
                    </button>
                    <button
                      onClick={() => { setModel('openai/gpt-5'); setShowModelSelector(false) }}
                      className={`w-full text-left px-3 py-1.5 text-xs rounded hover:bg-background-hover ${model === 'openai/gpt-5' ? 'bg-accent/20 text-accent' : ''}`}
                    >
                      GPT-5
                    </button>
                    <button
                      onClick={() => { setModel('anthropic/claude-3.5-sonnet'); setShowModelSelector(false) }}
                      className={`w-full text-left px-3 py-1.5 text-xs rounded hover:bg-background-hover ${model === 'anthropic/claude-3.5-sonnet' ? 'bg-accent/20 text-accent' : ''}`}
                    >
                      Claude 3.5 Sonnet
                    </button>
                    <button
                      onClick={() => { setModel('openai/gpt-4o'); setShowModelSelector(false) }}
                      className={`w-full text-left px-3 py-1.5 text-xs rounded hover:bg-background-hover ${model === 'openai/gpt-4o' ? 'bg-accent/20 text-accent' : ''}`}
                    >
                      GPT-4o
                    </button>

                    {/* Premium Affordable */}
                    <div className="px-2 py-1 text-[10px] font-semibold text-foreground-muted mt-2">⭐ Premium Affordable</div>
                    <button
                      onClick={() => { setModel('google/gemini-2.5-pro'); setShowModelSelector(false) }}
                      className={`w-full text-left px-3 py-1.5 text-xs rounded hover:bg-background-hover ${model === 'google/gemini-2.5-pro' ? 'bg-accent/20 text-accent' : ''}`}
                    >
                      Gemini 2.5 Pro
                    </button>
                    <button
                      onClick={() => { setModel('google/gemini-2.5-flash'); setShowModelSelector(false) }}
                      className={`w-full text-left px-3 py-1.5 text-xs rounded hover:bg-background-hover ${model === 'google/gemini-2.5-flash' ? 'bg-accent/20 text-accent' : ''}`}
                    >
                      Gemini 2.5 Flash
                    </button>
                    <button
                      onClick={() => { setModel('x-ai/grok-2-1212'); setShowModelSelector(false) }}
                      className={`w-full text-left px-3 py-1.5 text-xs rounded hover:bg-background-hover ${model === 'x-ai/grok-2-1212' ? 'bg-accent/20 text-accent' : ''}`}
                    >
                      Grok 2
                    </button>

                    {/* Ultra-Cheap */}
                    <div className="px-2 py-1 text-[10px] font-semibold text-foreground-muted mt-2">💰 Ultra-Cheap</div>
                    <button
                      onClick={() => { setModel('groq/llama-3.3-70b-versatile'); setShowModelSelector(false) }}
                      className={`w-full text-left px-3 py-1.5 text-xs rounded hover:bg-background-hover ${model === 'groq/llama-3.3-70b-versatile' ? 'bg-accent/20 text-accent' : ''}`}
                    >
                      Groq Llama 3.3 70B (US)
                    </button>
                    <button
                      onClick={() => { setModel('deepseek/deepseek-chat'); setShowModelSelector(false) }}
                      className={`w-full text-left px-3 py-1.5 text-xs rounded hover:bg-background-hover ${model === 'deepseek/deepseek-chat' ? 'bg-accent/20 text-accent' : ''}`}
                    >
                      DeepSeek Chat
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Web Search Toggle - Only show in brainstorm mode */}
            {mode === 'brainstorm' && (
              <>
                <button
                  onClick={() => setWebSearchEnabled(!webSearchEnabled)}
                  className={`px-2 py-1 text-xs flex items-center gap-1 rounded border transition-colors ${webSearchEnabled
                    ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                    : 'border-border text-foreground-muted hover:text-foreground hover:border-accent/50'
                    }`}
                  title={
                    webSearchEnabled
                      ? 'Web search enabled - Model can access real-time information via search APIs'
                      : 'Enable web search for real-time information (journalism, research, current events). Requires search API key in Settings.'
                  }
                >
                  <Globe className="w-3 h-3" />
                  <span>Web</span>
                  {webSearchEnabled && <span className="text-[10px]">✓</span>}
                </button>

                {/* Git Tools Toggle - Only show if project has GitHub repo */}
                {hasProject && (
                  <button
                    onClick={() => setGitToolsEnabled(!gitToolsEnabled)}
                    className={`px-2 py-1 text-xs flex items-center gap-1 rounded border transition-colors ${gitToolsEnabled
                      ? 'bg-green-500/20 border-green-500/50 text-green-400'
                      : 'border-border text-foreground-muted hover:text-foreground hover:border-accent/50'
                      }`}
                    title={
                      gitToolsEnabled
                        ? 'Git tools enabled - Model can commit and push changes to GitHub (with your confirmation)'
                        : 'Enable git tools to allow model to commit and push changes. Requires GitHub token in Settings.'
                    }
                  >
                    <GitBranch className="w-3 h-3" />
                    <span>Git</span>
                    {gitToolsEnabled && <span className="text-[10px]">✓</span>}
                  </button>
                )}

                {/* File Tools Toggle - Only show if project exists */}
                {hasProject && (
                  <button
                    onClick={() => setFileToolsEnabled(!fileToolsEnabled)}
                    className={`px-2 py-1 text-xs flex items-center gap-1 rounded border transition-colors ${fileToolsEnabled
                      ? 'bg-purple-500/20 border-purple-500/50 text-purple-400'
                      : 'border-border text-foreground-muted hover:text-foreground hover:border-accent/50'
                      }`}
                    title={
                      fileToolsEnabled
                        ? 'File tools enabled - Model can create, update, delete, and rename files (with your confirmation)'
                        : 'Enable file tools to allow model to manage project files'
                    }
                  >
                    <FileText className="w-3 h-3" />
                    <span>Files</span>
                    {fileToolsEnabled && <span className="text-[10px]">✓</span>}
                  </button>
                )}

                {/* Document Editor Toggle */}
                <button
                  onClick={() => setShowDocumentEditor(!showDocumentEditor)}
                  className={`px-2 py-1 text-xs flex items-center gap-1 rounded border transition-colors ${showDocumentEditor
                    ? 'bg-orange-500/20 border-orange-500/50 text-orange-400'
                    : 'border-border text-foreground-muted hover:text-foreground hover:border-accent/50'
                    }`}
                  title={
                    showDocumentEditor
                      ? 'Document editor enabled - Split screen with chat on left, editor on right'
                      : 'Enable document editor for split-screen editing while chatting'
                  }
                >
                  <Edit3 className="w-3 h-3" />
                  <span>Editor</span>
                  {showDocumentEditor && <span className="text-[10px]">✓</span>}
                </button>
              </>
            )}

            <button
              onClick={handleClearChat}
              className="btn-secondary px-2 py-1 text-xs flex items-center gap-1 hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-500"
              title="Clear chat history (removes all messages and attached files)"
            >
              <RotateCcw className="w-3 h-3" />
              <span>Clear</span>
            </button>
          </div>
          <div className="flex items-center gap-4 text-xs text-foreground-muted">
            <div className="flex items-center gap-1">
              <JudgeLogo className="w-3 h-3 text-yellow-400" />
              <span>Score: {judgeResult ? judgeResult.score.toFixed(1) : '--'}</span>
            </div>
            <div className="flex items-center gap-1">
              <Hash className="w-3 h-3" />
              <span>Runs: {metricsHistory.length}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-accent">Total Cost: ${cumulativeCost.toFixed(4)}</span>
              <span className="text-foreground-tertiary">({cumulativeTokens.toLocaleString()} tokens)</span>
            </div>
            {metricsHistory.length > 0 && (
              <span className="text-foreground-tertiary">
                Latest: {(metricsHistory[metricsHistory.length - 1].latency / 1000).toFixed(1)}s, ${metricsHistory[metricsHistory.length - 1].cost.toFixed(4)}
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border bg-background-secondary">
          <div className="flex items-center justify-between">
            <div className="flex">
              {[
                { name: 'Chat', tooltip: 'Orchestrator chat - Send requests and get AI responses' },
                { name: 'Inflate', tooltip: 'Convert MOTH format to readable Markdown' },
                { name: 'Preview', tooltip: 'Preview inflated Markdown as formatted document' },
                { name: 'Rubric', tooltip: 'Configure judging criteria and scoring rubrics' },
                { name: 'Tests', tooltip: 'Run automated tests on your work' },
                { name: 'Diff', tooltip: 'Compare versions and review changes' },
                { name: 'RAG', tooltip: 'Ask questions about your codebase using RAG' },
              ].map((tab) => (
                <button
                  key={tab.name}
                  onClick={() => onSetActiveTab(tab.name)}
                  className={`px-4 py-2 text-sm border-b-2 transition-colors ${activeTab === tab.name
                    ? 'border-accent text-accent'
                    : 'border-transparent text-foreground-muted hover:text-foreground'
                    }`}
                  title={tab.tooltip}
                >
                  {tab.name}
                </button>
              ))}
            </div>
            {activeTab === 'Chat' && messages.length > 0 && (
              <button
                onClick={onSaveOrchestratorChat}
                className="mr-2 px-3 py-1 text-xs bg-accent/10 hover:bg-accent/20 text-accent rounded transition-colors flex items-center gap-1"
                title="Save chat to project files"
              >
                <Save className="w-3 h-3" />
                Save Chat
              </button>
            )}
          </div>
        </div>

        {/* Tab content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {activeTab === 'Chat' && (
            <div className="flex-1 flex overflow-hidden">
              {/* Chat section (left side or full width) */}
              <div className={`flex flex-col overflow-hidden ${showDocumentEditor && mode === 'brainstorm' ? 'w-1/2 border-r border-border' : 'flex-1'}`}>
                {/* Chat messages */}
                <div className="flex-1 overflow-auto p-4 space-y-3">
                  {/* Filter out system messages from display */}
                  {messages.filter(m => m.role !== 'system').length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center text-foreground-muted">
                        <p className="text-lg font-title mb-2">What do you want to plan, build, write, learn?</p>
                        <p className="text-xs">Start a conversation with the Orchestrator</p>
                      </div>
                    </div>
                  ) : (
                    messages.filter(m => m.role !== 'system').map((message) => (
                      <div key={message.id} className="card p-3 relative">
                        <div className="text-xs text-foreground-muted mb-1 flex items-center gap-2">
                          {message.role === 'user' && 'You'}
                          {message.role === 'assistant' && (
                            <>
                              <span>Orchestrator</span>
                              {message.model && (
                                <span className="text-foreground-tertiary">
                                  ({message.model.split('/').pop()?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())})
                                </span>
                              )}
                            </>
                          )}
                          {message.isStreaming && (
                            <span className="flex items-center gap-1">
                              <Loader2 className="w-3 h-3 animate-spin text-accent" />
                              <span className="text-accent">Processing...</span>
                            </span>
                          )}
                        </div>
                        {message.role === 'assistant' ? (
                          <div className="text-sm pr-24 prose prose-invert prose-sm max-w-none">
                            <ReactMarkdown>{getMessageText(message.content)}</ReactMarkdown>
                          </div>
                        ) : (
                          <p className="text-sm whitespace-pre-wrap pr-24">{getMessageText(message.content)}</p>
                        )}

                        {/* Show attached files as chips */}
                        {message.attachedFiles && message.attachedFiles.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {message.attachedFiles.map((file, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-1.5 bg-accent/10 border border-accent/30 rounded px-2 py-1 text-xs"
                                title={file.path}
                              >
                                {file.type === 'image' ? (
                                  <img
                                    src={file.content}
                                    alt={file.path}
                                    className="w-8 h-8 object-cover rounded"
                                    title={`${file.path} (${file.mimeType})`}
                                  />
                                ) : (
                                  <FileCode className="w-3 h-3 text-accent" />
                                )}
                                <span className="text-foreground-muted max-w-[150px] truncate">
                                  {file.path.split('/').pop()}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                        {message.role === 'assistant' && message.content && (
                          <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-background/80 backdrop-blur-sm rounded p-1">
                            <button
                              onClick={() => onCopyPeerResponse(getMessageText(message.content))}
                              className="p-1 hover:bg-background-tertiary rounded transition-colors"
                              title="Copy to clipboard"
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => onInflatePeerResponse('Orchestrator Response', getMessageText(message.content))}
                              className="p-1 hover:bg-background-tertiary rounded transition-colors"
                              title="Inflate to Markdown"
                            >
                              <FileDown className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => onSaveChatMessage(message)}
                              className="p-1 hover:bg-background-tertiary rounded transition-colors"
                              title="Save as MOTH"
                            >
                              <Save className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Chat input */}
                <div
                  className={`border-t border-border p-3 bg-background-secondary transition-colors ${isDraggingOver ? 'bg-accent/10 border-accent' : ''
                    }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {isDraggingOver && (
                    <div className="text-xs text-accent mb-2 font-condensed">
                      📎 Drop file here to add to chat
                    </div>
                  )}

                  {/* Attached files display */}
                  {attachedFiles.length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs text-foreground-muted mb-1">
                        {attachedFiles.length} {attachedFiles.length === 1 ? 'file' : 'files'} attached
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {attachedFiles.map((file) => (
                          <div
                            key={file.path}
                            className="flex items-center gap-2 bg-accent/10 border border-accent/30 rounded px-2 py-1.5 text-xs group"
                          >
                            {file.type === 'image' ? (
                              <img
                                src={file.content}
                                alt={file.path}
                                className="w-10 h-10 object-cover rounded"
                                title={`${file.path} (${file.mimeType})`}
                              />
                            ) : (
                              <FileCode className="w-3.5 h-3.5 text-accent" />
                            )}
                            <span className="text-foreground max-w-[200px] truncate" title={file.path}>
                              {file.path.split('/').pop()}
                            </span>
                            <button
                              onClick={() => removeAttachedFile(file.path)}
                              className="opacity-60 hover:opacity-100 transition-opacity"
                              title="Remove file"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    {/* Mode Slider */}
                    <div className="flex items-stretch border border-border rounded-lg overflow-hidden bg-background-secondary h-[42px]">
                      <button
                        onClick={() => setMode('brainstorm')}
                        className={`px-3 text-xs font-medium transition-colors ${mode === 'brainstorm'
                          ? 'bg-accent text-background'
                          : 'text-foreground-muted hover:text-foreground hover:bg-background-tertiary'
                          }`}
                        title="Brainstorm mode - Natural conversation with the orchestrator"
                      >
                        Brainstorm
                      </button>
                      <button
                        onClick={() => setMode('peer-review')}
                        className={`px-3 text-xs font-medium transition-colors ${mode === 'peer-review'
                          ? 'bg-accent text-background'
                          : 'text-foreground-muted hover:text-foreground hover:bg-background-tertiary'
                          }`}
                        title="Peer Review mode - Structured multi-LLM review with MOTH format"
                      >
                        Peer Review
                      </button>
                    </div>

                    <input
                      type="text"
                      value={chatInput}
                      onChange={(e) => onSetChatInput(e.target.value)}
                      onPaste={handlePaste}
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter' && !e.shiftKey && !isLoading && hasProject) {
                          if (chatInput.trim() || attachedFiles.length > 0) {
                            e.preventDefault()
                            await handleSendWithAttachments()
                          }
                        }
                      }}
                      placeholder={hasProject ? "Type a message, paste a file path, or drag & drop..." : "Create or open a project to start chatting..."}
                      className="input flex-1 text-sm h-[42px]"
                      disabled={isLoading || !hasProject}
                    />
                    {isLoading ? (
                      <button
                        className="btn-secondary px-4 h-[42px] flex items-center gap-2 rounded-lg"
                        onClick={() => onStopStreaming()}
                      >
                        <Square className="w-4 h-4" />
                        <span className="text-sm">Stop</span>
                      </button>
                    ) : (
                      <button
                        className="btn-secondary w-[42px] h-[42px] rounded-lg flex items-center justify-center"
                        disabled={(!chatInput.trim() && attachedFiles.length === 0) || isOrchestrating || !hasProject}
                        onClick={handleSendWithAttachments}
                        title={hasProject ? `Send message to orchestrator (${mode === 'brainstorm' ? 'Brainstorm mode' : 'Peer Review mode'})` : "Create or open a project first"}
                      >
                        <ArrowUpCircle className="w-8 h-8" />
                      </button>
                    )}

                    {/* Toggles - Only show in peer review mode */}
                    {mode === 'peer-review' && (
                      <div className="flex flex-col gap-1.5 text-xs text-foreground-muted self-center">
                        {/* Include Docs Toggle */}
                        <div className="flex items-center gap-2 justify-end" title="When enabled, attached files are included in peer reviews">
                          <span>Include Docs</span>
                          <button
                            onClick={() => onSetIncludeDocs(!includeDocs)}
                            className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${includeDocs ? 'bg-accent' : 'bg-background-tertiary'
                              }`}
                            title={includeDocs ? 'Attached files will be sent to peers' : 'Attached files will not be sent to peers'}
                          >
                            <span
                              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${includeDocs ? 'translate-x-3.5' : 'translate-x-0.5'
                                }`}
                            />
                          </button>
                        </div>

                        {/* Auto Review Toggle */}
                        <div className="flex items-center gap-2 justify-end" title={mode === 'peer-review' ? 'Auto Review is always ON in Peer Review mode' : 'Automatically send responses to peers for review'}>
                          <span>Auto Review</span>
                          <button
                            onClick={() => {
                              if (mode !== 'peer-review') {
                                onSetAutoPeerReview(!autoPeerReview)
                              }
                            }}
                            className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${(autoPeerReview || mode === 'peer-review') ? 'bg-accent' : 'bg-background-tertiary'
                              } ${mode === 'peer-review' ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title={mode === 'peer-review' ? 'Auto Review is always ON in Peer Review mode' : (autoPeerReview ? 'Auto review is ON - responses sent to peers automatically' : 'Auto review is OFF - click "Send for Review" manually')}
                            disabled={mode === 'peer-review'}
                          >
                            <span
                              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${(autoPeerReview || mode === 'peer-review') ? 'translate-x-3.5' : 'translate-x-0.5'
                                }`}
                            />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Send for Review button - Only show in peer review mode */}
                    {mode === 'peer-review' && (
                      <button
                        className={`btn-primary px-4 h-[42px] flex items-center gap-2 ${autoPeerReview || isOrchestrating || !hasProject ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={(!chatInput.trim() && attachedFiles.length === 0) || isLoading || isOrchestrating || autoPeerReview || !hasProject}
                        onClick={async () => {
                          if ((chatInput.trim() || attachedFiles.length > 0) && !isLoading && !isOrchestrating && !autoPeerReview && hasProject) {
                            // If files are attached, show preview modal first
                            if (attachedFiles.length > 0) {
                              setPendingAction('orchestration')
                              setShowFilePreview(true)
                              return
                            }

                            // No files, send directly
                            const message = chatInput.trim()
                            onSetChatInput('')
                            setAttachedFiles([])
                            // Reset approvalSent when starting a NEW orchestration
                            onSetApprovalSent(false)
                            await onRunOrchestration(message, peers, rubricContent, judgeModel, undefined)
                          }
                        }}
                        title={!hasProject ? 'Create or open a project first' : autoPeerReview || mode === 'peer-review' ? 'Disabled - Auto Review is on' : 'Manually send current message to peers for review'}
                      >
                        <SendHorizontal className="w-4 h-4" />
                        <span className="text-sm">{isOrchestrating ? 'Running...' : 'Send for Review'}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Document Editor (right side) - Only show in brainstorm mode */}
              {showDocumentEditor && mode === 'brainstorm' && (
                <div className="w-1/2">
                  <BrainstormDocumentEditor
                    onClose={() => setShowDocumentEditor(false)}
                    onSave={async (content, filePath) => {
                      setDocumentContent(content)

                      // Save to project files if we have a file path
                      if (filePath && hasProject) {
                        try {
                          const { useProjectStore } = await import('@/ui/stores/project-store')
                          await useProjectStore.getState().updateFile(filePath, content)
                          console.log('[Document Editor] ✅ Saved to project:', filePath)
                        } catch (error) {
                          console.error('[Document Editor] Failed to save:', error)
                        }
                      } else {
                        console.log('[Document Editor] Saved:', documentFileName, content.length, 'chars')
                      }
                    }}
                    onFileLoaded={(filePath, content) => {
                      // Update document state
                      setDocumentContent(content)
                      setDocumentFileName(filePath)

                      // Enable file tools automatically
                      setFileToolsEnabled(true)

                      // Send system message to LLM about editing this file
                      const systemMessage = `You are now helping the user edit the file: ${filePath}

The file content has been loaded into the document editor on the right side of the screen.

IMPORTANT:
- You have file editing tools enabled (create_file, update_file, delete_file, rename_file)
- When the user asks you to make changes to this file, use the update_file tool
- The file path is: ${filePath}
- You can see the current content and should help the user modify it
- Make precise, targeted edits based on the user's requests
- Always confirm what changes you're making before executing them

The user can now ask you to make changes to this file, and you should use the file tools to edit it in real-time.`

                      // Add system message to chat
                      useChatStore.getState().addMessage({
                        role: 'user',
                        content: `Please update the file ${filePath} based on the peer review feedback.`
                      })

                      console.log('[Document Editor] 📄 Loaded file for editing:', filePath)
                      console.log('[Document Editor] 🔧 File tools enabled')
                      console.log('[Document Editor] 💬 System message sent to LLM')
                    }}
                    initialContent={documentContent}
                    fileName={documentFileName}
                  />
                </div>
              )}
            </div>
          )}

          {activeTab === 'Inflate' && (
            <div className="flex-1 flex overflow-hidden">
              {/* History sidebar */}
              <div className="w-64 border-r border-border bg-background-secondary flex flex-col">
                <div className="p-3 border-b border-border">
                  <h3 className="text-xs text-foreground-secondary uppercase tracking-wide">Inflated Docs</h3>
                </div>
                <div className="flex-1 overflow-auto p-2 space-y-1">
                  {Object.keys(inflatedDocs).length === 0 ? (
                    <p className="text-xs text-foreground-muted italic p-2">No inflated docs yet</p>
                  ) : (
                    Object.keys(inflatedDocs).sort().reverse().map((fileName) => (
                      <button
                        key={fileName}
                        onClick={() => onHandleLoadInflatedDoc(fileName)}
                        className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${selectedInflatedDoc === fileName
                          ? 'bg-accent/20 text-accent'
                          : 'hover:bg-background-tertiary text-foreground-secondary'
                          }`}
                      >
                        {fileName.replace('inflated-', '').replace('.md', '')}
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Content area */}
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="border-b border-border bg-background-secondary p-2 flex justify-between items-center gap-2">
                  <button
                    onClick={onHandleInflate}
                    className="btn-primary px-3 py-1.5 text-xs flex items-center gap-2"
                    disabled={isInflating}
                    title="Convert MOTH format to beautiful, human-readable Markdown"
                  >
                    {isInflating ? 'Inflating...' : 'Inflate Current Thread'}
                  </button>
                  <button
                    onClick={onCopyInflatedMarkdown}
                    className="btn-ghost px-3 py-1.5 text-xs flex items-center gap-2"
                    disabled={!inflatedMarkdown}
                    title="Copy the inflated markdown to clipboard"
                  >
                    <Copy className="w-3 h-3" />
                    {copiedInflate ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="flex-1 overflow-auto p-4">
                  {inflatedMarkdown ? (
                    <pre className="text-xs font-mono whitespace-pre-wrap">{inflatedMarkdown}</pre>
                  ) : (
                    <p className="text-foreground-muted italic text-sm">
                      No inflated content yet. Click "Inflate Current Thread" to convert MOTH to Markdown.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'Preview' && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="border-b border-border bg-background-secondary p-2 flex justify-end gap-2">
                <button
                  onClick={onCopyInflatedMarkdown}
                  className="btn-ghost px-3 py-1.5 text-xs flex items-center gap-2"
                  disabled={!inflatedMarkdown}
                >
                  <Copy className="w-3 h-3" />
                  {copiedPreview ? 'Copied!' : 'Copy Markdown'}
                </button>
                <button
                  onClick={onExportPDF}
                  className="btn-primary px-3 py-1.5 text-xs flex items-center gap-2"
                  disabled={!inflatedMarkdown || isExportingPDF}
                  title="Export the inflated markdown as a PDF document"
                >
                  {isExportingPDF ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      <FileDown className="w-3 h-3" />
                      Export PDF
                    </>
                  )}
                </button>
              </div>
              <div ref={previewRef} className="flex-1 overflow-auto p-6 bg-white text-gray-900">
                {inflatedMarkdown ? (
                  <div className="prose prose-sm max-w-none font-body">
                    <ReactMarkdown
                      components={{
                        h1: ({ node, ...props }) => <h1 className="font-title text-gray-900" {...props} />,
                        h2: ({ node, ...props }) => <h2 className="font-title text-gray-900" {...props} />,
                        h3: ({ node, ...props }) => <h3 className="font-title text-gray-900" {...props} />,
                        h4: ({ node, ...props }) => <h4 className="font-title text-gray-900" {...props} />,
                        h5: ({ node, ...props }) => <h5 className="font-title text-gray-900" {...props} />,
                        h6: ({ node, ...props }) => <h6 className="font-title text-gray-900" {...props} />,
                        p: ({ node, ...props }) => <p className="font-body text-gray-900" {...props} />,
                        code: ({ node, ...props }) => <code className="font-mono bg-gray-100 text-blue-600 px-1 py-0.5 rounded" {...props} />,
                        pre: ({ node, ...props }) => <pre className="font-mono bg-gray-100 text-gray-900 p-4 rounded overflow-x-auto" {...props} />,
                        a: ({ node, ...props }) => <a className="text-blue-600 hover:underline" {...props} />,
                        ul: ({ node, ...props }) => <ul className="font-body text-gray-900" {...props} />,
                        ol: ({ node, ...props }) => <ol className="font-body text-gray-900" {...props} />,
                        li: ({ node, ...props }) => <li className="font-body text-gray-900" {...props} />,
                        blockquote: ({ node, ...props }) => <blockquote className="font-body text-gray-700 border-l-4 border-blue-500 pl-4" {...props} />,
                      }}
                    >
                      {inflatedMarkdown}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-gray-500 italic font-body">
                    No preview available. Inflate content first in the Inflate tab.
                  </p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'RAG' && (
            <RAGChat onHighlightFile={onHighlightFile} onOpenFile={onOpenFile} />
          )}

          {(activeTab === 'Rubric' || activeTab === 'Tests' || activeTab === 'Diff') && (
            <div className="flex-1 flex items-center justify-center text-foreground-muted">
              <p className="text-sm">Tab content for {activeTab} (to be implemented)</p>
            </div>
          )}
        </div>
      </section>

      {/* File Preview Modal */}
      {showFilePreview && (
        <FilePreviewModal
          files={attachedFiles}
          onConfirm={handleConfirmFilePreview}
          onCancel={() => {
            setShowFilePreview(false)
            setPendingAction(null)
          }}
          onFilesChanged={onFilesChanged}
        />
      )}

      {/* Handover Modal - Brainstorm to Peer Review */}
      {showHandoverModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-border rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="border-b border-border p-4">
              <h2 className="text-lg font-semibold">Switching to Peer Review Mode</h2>
              <p className="text-sm text-foreground-muted mt-1">
                Your brainstorm conversation has been compiled into a project document. Review and edit it below, then:
              </p>
              <ul className="text-sm text-foreground-muted mt-2 ml-4 space-y-1">
                <li>• <strong>Approve & Send to Peers</strong> - Switches to Peer Review mode and sends this to your panel of AI reviewers</li>
                <li>• <strong>Brainstorm More</strong> - Stay in Brainstorm mode and continue refining with natural conversation</li>
              </ul>
            </div>

            <div className="flex-1 p-4 overflow-auto">
              <textarea
                value={compiledDocument}
                onChange={(e) => setCompiledDocument(e.target.value)}
                className="input w-full h-full min-h-[400px] font-mono text-sm"
                placeholder="Compiled document will appear here..."
              />
            </div>

            <div className="border-t border-border p-4 flex justify-between items-center">
              <button
                onClick={() => {
                  const feedback = prompt('Enter feedback or adjustments you want (leave empty to continue brainstorming):')
                  if (feedback !== null) {
                    brainstormMore(feedback || 'Let\'s continue refining this.')
                  }
                }}
                className="btn-secondary px-4 py-2"
                title="Add feedback and continue brainstorming in natural language before peer review"
              >
                Brainstorm More
              </button>
              <div className="flex gap-2">
                <button
                  onClick={closeHandoverModal}
                  className="btn-secondary px-4 py-2"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => await approveHandover()}
                  className="btn-primary px-4 py-2"
                  disabled={!compiledDocument.trim()}
                  title="Switch to Peer Review mode and send this document to your configured AI reviewers for structured feedback"
                >
                  Approve & Send to Peers
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

